import * as React from "react";
import Button from "@sproutsocial/seeds-react-button";
import Drawer from "@sproutsocial/seeds-react-drawer";
import Icon from "@sproutsocial/seeds-react-icon";
// eslint-disable-next-line import/no-deprecated
import Text from "@sproutsocial/seeds-react-text";
import type { TypeDrawerActionProps } from "@sproutsocial/seeds-react-drawer";

interface TypePanelMobileActionsHeaderProps {
  title?: string;
  titleId?: string;
  actions: TypeDrawerActionProps[];
}

/**
 * Mobile bottom-sheet header used by Panel when `actionsInHeader` is set and
 * no custom `header` is provided. Renders the title on the left and the
 * forwarded actions plus a close button as pills on the right — mirrors the
 * pattern in `seeds-react-peek-in/PeekIn.tsx`. The Drawer's floating rail is
 * suppressed in this combination (`actionsInHeader` opts out), so without this
 * header the actions would render nowhere.
 *
 * This header is composed internally by Panel (it receives no consumer
 * styled-system props), so its chrome is emitted directly as `seeds-panel-*`
 * classes from panel.css rather than through a styled `Box`.
 */
export const PanelMobileActionsHeader = ({
  title,
  titleId,
  actions,
}: TypePanelMobileActionsHeaderProps) => (
  <Drawer.Header
    render={(ctx) => (
      <div className="seeds-panel-mobile-actions-header">
        <Text
          as="h2"
          fontSize={400}
          fontWeight="semibold"
          color="text.headline"
          id={titleId}
        >
          {title}
        </Text>
        <div className="seeds-panel-mobile-actions-buttons">
          {actions.map((action, i) => (
            <Button
              key={i}
              appearance="pill"
              aria-label={action["aria-label"]}
              onClick={action.onClick}
              disabled={action.disabled}
            >
              {action.iconName && <Icon aria-hidden name={action.iconName} />}
            </Button>
          ))}
          <Button
            appearance="pill"
            aria-label={ctx.closeButtonLabel}
            onClick={ctx.onClose}
          >
            <Icon aria-hidden name="x-outline" />
          </Button>
        </div>
      </div>
    )}
  />
);

PanelMobileActionsHeader.displayName = "PanelMobileActionsHeader";
